import { reportMilestone } from "utils/milestones";
import React from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import EventDetailPage from "./EventDetailPage";
import eventsAPI from "restApi/eventsApi";
import { TEAM_COLORS as teamColors } from "CONSTANTS";
import { defaultEventTimes } from "utils/eventTimes";

// TEAM_COLORS is a fixed-size array indexed by team.id - 1 - a team id
// past its end has no entry. Mirrors Upcoming.jsx's teamColorFor: without
// this guard, a team past that range threw inside the .then() below,
// which the .catch() then misreported as "can't find this event."
const teamColorFor = (team) => {
  const color = team && teamColors[team.id - 1];
  return color ? color.main : undefined;
};

// Also handles /events/new (matched by the same /events/:id route, with
// id === "new" as the create sentinel - deliberately NOT a separate
// route entry pointing at this same component: react-router's Switch
// picks a child by which Route *element* matches, not which resolved
// component does, so swapping from a "/events/new" Route match to a
// "/events/:id" Route match - even for the same component - unmounts
// and remounts this whole subtree. ensureCreated's history.replace
// below needs that swap to happen in place, with no remount, since its
// own promise chain is still running against this component instance
// when it fires). Creating a new event is meant to be exactly the same
// experience as viewing/editing an existing one - same page, same
// inline fields, same setlist controls - so rather than a separate
// create-only page, this container just seeds a local, not-yet-
// persisted eventModal (id: null, a "New event" dummy title so an edit
// to some other field first still has a valid name to create with) and
// lazily creates the real event on the first actual edit (see
// ensureCreated below). Leaving before making any edit at all means
// ensureCreated never runs, so nothing is ever created.
export default () => {
  const { id } = useParams();
  const isCreate = id === "new";
  const history = useHistory();
  const location = useLocation();
  // A new event starts at noon, two hours long, on the day picked on the
  // calendar (or today) - see utils/eventTimes.js.
  const newEventTimes = defaultEventTimes(location.state);
  const [eventModal, setEventModal] = React.useState(
    isCreate
      ? {
          loading: false,
          id: null,
          team: null,
          title: "New event",
          details: "",
          // The day picked on the calendar grid (see Calendar.jsx's `select`
          // handler) decides the date; the time defaults to noon-2pm, not
          // the midnight a clicked day arrives as.
          timeStart: newEventTimes.timeStart,
          timeEnd: newEventTimes.timeEnd,
          songs: [],
          notes: [],
          setlistTemplate: null,
          spotify_playlist_id: "",
          status: "draft",
        }
      : {
          loading: true,
          id: parseInt(id),
          team: null,
          title: null,
          details: "",
          timeStart: null,
          timeEnd: null,
          songs: [],
          notes: [],
          setlistTemplate: null,
          spotify_playlist_id: "",
          status: "draft",
        }
  );

  React.useEffect(() => {
    if (isCreate) return;
    eventsAPI
      .get(id)
      .then((res) => {
        const team = res.data.team && {
          ...res.data.team,
          color: teamColorFor(res.data.team),
        };
        setEventModal({
          loading: false,
          id: parseInt(id),
          team,
          title: res.data.name,
          details: res.data.details,
          timeStart: new Date(res.data.time_start),
          timeEnd: new Date(res.data.time_end),
          songs: res.data.song_list,
          notes: res.data.event_notes,
          setlistTemplate: res.data.setlist_template,
          spotify_playlist_id: res.data.spotify_playlist_id,
          status: res.data.status,
        });
        reportMilestone("opened_event");
      })
      .catch((err) => {
        // Signed out (an expired session): the app is about to send them to
        // the login page, so don't flash "can't find this event" meanwhile.
        if (err && err.response && err.response.status === 401) return;
        // Most often this is someone switching to a team that doesn't
        // have this event (events are scoped by whichever team is
        // currently active) - without this, the page was stuck on its
        // loading spinner forever instead of saying so. A draft's link
        // gets a distinguishable `code` from EventViewSet.retrieve()
        // (it exists, it's just not published) - EventDetailPage shows
        // that case honestly instead of the generic "can't find this
        // event" a bare 404 would otherwise read as.
        const code = err && err.response && err.response.data && err.response.data.code;
        setEventModal((prev) => ({
          ...prev,
          loading: false,
          notFound: true,
          draftNotPublished: code === "draft_not_published",
        }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Every mutating handler below calls this first. Once eventModal.id is
  // real, it's a no-op (resolves immediately) - existing-event editing
  // is completely unaffected. Before that, it creates the event with
  // whatever's currently in the local draft (the dummy title, unless
  // already renamed) and swaps the URL to the real /events/:id via
  // `replace` (not `push`, so Back still leaves the same way it would
  // from a real event, and a refresh afterward re-fetches normally
  // instead of re-seeding a second blank draft). A ref (not state)
  // memoizes the in-flight request so two edits fired in quick
  // succession before the first POST resolves can't create it twice.
  const creatingRef = React.useRef(null);
  const ensureCreated = () => {
    if (eventModal.id) return Promise.resolve(eventModal.id);
    if (creatingRef.current) return creatingRef.current;
    creatingRef.current = eventsAPI
      .createEvent({
        name: eventModal.title,
        details: eventModal.details,
        time_start: eventModal.timeStart,
        time_end: eventModal.timeEnd,
        // Omitted (not explicitly null) unless the user already chose
        // one - EventViewSet.create() only auto-applies the team's
        // default template when the key is absent entirely, same as a
        // normal fresh event created any other way. If they did pick
        // one before this first edit, `handleChangeTemplate` will still
        // be the call that actually triggered ensureCreated, and its own
        // follow-up PATCH (below) applies that real choice regardless.
        ...(eventModal.setlistTemplate ? { setlist_template: eventModal.setlistTemplate.id } : {}),
        spotify_playlist: !!eventModal.spotify_playlist_id,
        status: eventModal.status,
        songs: [],
      })
      .then((res) => {
        const newId = res.data.id;
        // EventSerializer's own create() response carries setlist_template
        // as a plain id, not the nested {id, name, sections} object this
        // page needs (see handleChangeTemplate's identical situation
        // below) - refetching once via EventDetailSerializer hydrates
        // that correctly, along with team, in one shot.
        return eventsAPI.get(newId).then((detail) => {
          const team = detail.data.team && {
            ...detail.data.team,
            color: teamColorFor(detail.data.team),
          };
          history.replace(`/events/${newId}`, location.state);
          setEventModal((prev) => ({
            ...prev,
            id: newId,
            team,
            setlistTemplate: detail.data.setlist_template,
          }));
          return newId;
        });
      });
    return creatingRef.current;
  };

  // "Back" returns to whichever page linked here (Upcoming or Calendar
  // pass their own location as state.from when navigating in) - falls
  // back to Upcoming for a direct/shared link with no app history to
  // return to. Also doubles as "discard" for a not-yet-created event -
  // ensureCreated never having run means there's nothing to discard.
  const closeEventModal = () => {
    history.push((location.state && location.state.from) || "/upcoming");
  };


  const handleUnassignEvent = (song, event) => {
    return ensureCreated().then((realId) =>
      eventsAPI
        .patchEvent(realId, {
          songs: eventModal.songs.filter((s) => s.id !== song.id).map((s) => s.id),
        })
        .then(() => {
          setEventModal((prev) => ({
            ...prev,
            songs: prev.songs.filter((s) => s.id !== song.id),
          }));
        })
    );
  };

  // Drag-and-drop needs the reorder to show up immediately (the library
  // already visually settled the item where it was dropped - waiting on
  // the network round-trip before updating state would show it snap
  // back, then jump again once the request resolves). Update local state
  // right away with the already-known result, and persist it in the
  // background. Returns the persist promise (not swallowed) so the page
  // can flag a failed save - the arrangement wouldn't survive a refresh
  // otherwise, with nothing telling the user that happened. `items` is
  // the combined, already-reordered songs+notes list built in
  // EventDetailPage.jsx, each entry tagged with `itemType`.
  const handleReorderItems = (items) => {
    setEventModal((prev) => ({
      ...prev,
      songs: items.filter((item) => item.itemType === "song"),
      notes: items.filter((item) => item.itemType === "note"),
    }));
    return ensureCreated().then((realId) =>
      eventsAPI.reorderItems(
        realId,
        items.map((item, index) => ({
          type: item.itemType,
          id: item.id,
          section: item.section,
          order: index,
        }))
      )
    );
  };

  // Unlike reorder above, this can't be predicted client-side - each
  // template remembers its own arrangement server-side (see
  // EventSongPlacement/EventNotePlacement), so which section/order every
  // song and note ends up at depends on that template's own history,
  // not anything this request sends - so it waits for the real response
  // rather than updating optimistically. Refetches afterward
  // instead of using the PATCH response directly: EventViewSet.update()
  // responds via EventSerializer, whose setlist_template is a plain id
  // (write-shaped, matching what this same request just sent) - not the
  // nested {id, name, sections} object EventDetailSerializer returns and
  // everything in EventDetailPage (grouping, the template picker) expects.
  const handleChangeTemplate = (templateId) => {
    return ensureCreated().then((realId) =>
      eventsAPI.patchEvent(realId, { setlist_template: templateId }).then(() => {
        return eventsAPI.get(realId).then((res) => {
          setEventModal((prev) => ({
            ...prev,
            setlistTemplate: res.data.setlist_template,
            songs: res.data.song_list,
            notes: res.data.event_notes,
          }));
        });
      })
    );
  };

  // add-song/add-note/note edits all respond via EventDetailSerializer
  // directly (unlike the template PATCH above) - correctly nested
  // setlist_template already, safe to use as-is with no extra refetch.
  const applyEventResponse = (res) => {
    setEventModal((prev) => ({
      ...prev,
      setlistTemplate: res.data.setlist_template,
      songs: res.data.song_list,
      notes: res.data.event_notes,
    }));
  };

  const handleAddSong = (songId, sectionId) => {
    return ensureCreated().then((realId) => eventsAPI.addSong(realId, songId, sectionId).then(applyEventResponse));
  };

  const handleAddNote = (content, sectionId) => {
    return ensureCreated().then((realId) => eventsAPI.addNote(realId, content, sectionId).then(applyEventResponse));
  };

  const handleUpdateNote = (noteId, content) => {
    return ensureCreated().then((realId) => eventsAPI.updateNote(realId, noteId, content).then(applyEventResponse));
  };

  const handleDeleteNote = (noteId) => {
    return ensureCreated().then((realId) =>
      eventsAPI.deleteNote(realId, noteId).then(() => {
        setEventModal((prev) => ({
          ...prev,
          notes: prev.notes.filter((note) => note.id !== noteId),
        }));
      })
    );
  };

  // Desktop's inline-editable fields (name, start/end time, details,
  // Spotify/status) all go through this one generic PATCH - `fields`
  // uses the API's own key names (name/time_start/time_end/...) directly,
  // matching what patchEvent already expects. Always echoing back the
  // *current* spotify_playlist state (unless this call is the one
  // changing it) matters: EventViewSet.update() deletes the team's
  // Spotify playlist on any PATCH that omits spotify_playlist while one
  // already exists - see the matching comment there. The response is
  // EventSerializer-shaped (not EventDetailSerializer), so only the
  // fields it actually carries are merged back in - setlist_template,
  // song_list and notes stay exactly as they already are locally. On the
  // very first edit of a not-yet-created event, ensureCreated's own POST
  // still only has the stale local draft to work with (this function's
  // own `eventModal` closure hasn't picked up `fields` yet - that only
  // happens once this PATCH's response comes back) - so that first edit
  // costs an extra round trip (create with the dummy/prior values, then
  // immediately patch in the real ones) before settling on the correct
  // state. Every later edit is a single PATCH, same as an already-real
  // event.
  const handleUpdateEvent = (fields) => {
    return ensureCreated().then((realId) =>
      eventsAPI
        .patchEvent(realId, {
          spotify_playlist: !!eventModal.spotify_playlist_id,
          ...fields,
        })
        .then((res) => {
          setEventModal((prev) => ({
            ...prev,
            title: res.data.name,
            details: res.data.details,
            timeStart: new Date(res.data.time_start),
            timeEnd: new Date(res.data.time_end),
            spotify_playlist_id: res.data.spotify_playlist_id,
            status: res.data.status,
          }));
        })
    );
  };

  const handleDeleteEvent = () => {
    return eventsAPI.deleteEvent(eventModal.id).then(() => {
      history.push("/upcoming");
    });
  };

  // Manual override for the automatic (cooldown-gated) push in
  // maybe_notify_event (song_proposals/push.py) - only ever reachable
  // once the event is real (see EventDetailPage.jsx's gating on
  // eventModal.id), so no ensureCreated() wrapping needed here.
  const handleNotifyTeam = () => eventsAPI.notifyTeam(eventModal.id);

  const menuActions = {
    // Editing is its own page now (SongMenuButton navigates straight
    // there) - this key's value is only ever checked for presence, to
    // gate whether the Edit item shows.
    edit: true,
    unassignEvent: handleUnassignEvent,
  };

  return (
    <EventDetailPage
      eventModal={eventModal}
      closeEventModal={closeEventModal}
      menuActions={menuActions}
      onReorderItems={handleReorderItems}
      onChangeTemplate={handleChangeTemplate}
      onAddSong={handleAddSong}
      onAddNote={handleAddNote}
      onUpdateNote={handleUpdateNote}
      onDeleteNote={handleDeleteNote}
      onUpdateEvent={handleUpdateEvent}
      onDeleteEvent={handleDeleteEvent}
      onNotifyTeam={handleNotifyTeam}
    />
  );
};
